<template>
    <section class="box showcase">
        <!-- ref="acts" on every iteration is Vue's standard v-for + ref
             pattern: it collects every element that sets it into ONE array,
             $refs.acts, in source order — not four separate refs. -->
        <div
            v-for="(feature, index) in features"
            :key="feature.title"
            ref="acts"
            class="showcase-act"
            :class="{ 'is-flipped': index % 2 === 1, 'is-live': revealed[index] }">
            <!-- `is-desktop`, not Bulma's default tablet break: at 769–1023
                 the copy column is only ~316px, which is too narrow for these
                 headings and left every other one wrapping to two lines. Side
                 by side starts where there's room for it. -->
            <div class="columns is-vcentered is-variable is-6 is-desktop">
                <div class="column is-5-desktop showcase-copy">
                    <p class="showcase-eyebrow">
                        <b-icon :icon="feature.icon" size="is-small" />
                        Included with Premium
                    </p>
                    <h3 class="title is-4 showcase-title">{{ feature.title }}</h3>
                    <p class="showcase-body">{{ feature.body }}</p>
                </div>

                <div class="column is-7-desktop showcase-stage">
                    <!-- Double XP: the same game, paid out twice. -->
                    <div v-if="feature.visual === 'xp'" class="demo demo-xp">
                        <div v-for="bar in xpBars" :key="bar.label" class="xp-row">
                            <p class="xp-label">
                                {{ bar.label }}
                                <b-tag v-if="bar.premium" type="is-gold" size="is-small">2&times;</b-tag>
                            </p>
                            <div class="xp-track">
                                <div
                                    class="xp-fill"
                                    :class="{ 'is-premium': bar.premium }"
                                    :style="{ width: revealed[index] ? bar.width : '0%' }" />
                            </div>
                            <p class="xp-value">{{ bar.value.toLocaleString() }} XP</p>
                        </div>
                        <p class="help demo-caption">One evening of Cards Against Humanity.</p>
                    </div>

                    <!-- 40 more rewards: the same 50 levels, filled in. -->
                    <div v-else-if="feature.visual === 'track'" class="demo demo-track">
                        <div v-for="row in trackRows" :key="row.label" class="track-row">
                            <p class="track-label">{{ row.label }}</p>
                            <ul class="track-nodes">
                                <li
                                    v-for="(filled, i) in row.nodes"
                                    :key="i"
                                    class="track-node"
                                    :class="{ 'is-filled': filled, 'is-premium': row.premium }"
                                    :style="nodeDelay(i)" />
                            </ul>
                            <p class="track-count">{{ row.count }}</p>
                        </div>
                        <p class="help demo-caption">Rewards across a 50-level season.</p>
                    </div>

                    <!-- A crown by your name. -->
                    <div v-else-if="feature.visual === 'crown'" class="demo demo-crown">
                        <div class="crown-card">
                            <span class="crown-avatar" />
                            <span class="crown-name">
                                {{ displayName }}
                                <img
                                    class="crown-mark"
                                    src="@/assets/images/premium/crown.svg"
                                    alt="" />
                            </span>
                            <b-tag type="is-light" size="is-small">Level 27</b-tag>
                        </div>
                        <p class="help demo-caption">On your profile, and everywhere you place.</p>
                    </div>

                    <!-- Leaderboards: the pass's whole pitch is the standings.
                         Matched explicitly rather than left as a v-else, so a
                         feature added without a `visual` renders nothing
                         instead of quietly drawing a leaderboard beside copy
                         about something else. -->
                    <div v-else-if="feature.visual === 'leaderboard'" class="demo demo-board">
                        <ol class="board">
                            <li
                                v-for="(row, i) in board"
                                :key="row.name"
                                class="board-row"
                                :class="{ 'is-you': row.you }"
                                :style="rowShuffle(i)">
                                <span class="board-rank">{{ i + 1 }}</span>
                                <span class="board-name">{{ row.name }}</span>
                                <span class="board-wins">{{ row.wins }}</span>
                            </li>
                        </ol>
                        <p class="help demo-caption">Standings for your whole server, every season.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
/**
 * The part of the sales page that shows rather than tells.
 *
 * Every feature in PREMIUM.features gets a small, hand-drawn demo of the thing
 * it's promising — an XP bar paid out twice, a reward track filled in, a crown
 * landing on a name, a leaderboard sorting itself. They're CSS and SVG-free
 * markup rather than video or a library: four looping animations shouldn't
 * cost a megabyte, and the demos have to restyle with the rest of the page.
 *
 * Each demo runs once it's individually on screen (IntersectionObserver, one
 * per act), and runs once — a page of things pulsing forever is a page nobody
 * can read. It used to be one observer and one shared flag for the whole
 * section, which is wrong the moment the section is taller than the
 * viewport: whichever single point that shared observer watched was
 * necessarily too early for the acts below it and too late for the acts
 * already on screen above it. The leaderboard — the last act, and the
 * furthest from any one trigger point — was the one this broke worst: by the
 * time a visitor actually scrolled to it, its transition had finished
 * off-screen minutes... no, seconds earlier, so it never looked like it
 * animated at all. Now each act answers only for its own visibility.
 *
 * Under `prefers-reduced-motion` every demo paints its *finished* state
 * immediately: the information is the point, the motion is decoration, so
 * the reduced path loses the decoration and keeps the information. That's
 * the same bargain GameBubbles strikes on the profile.
 */

const XP_BASE = 1240

export default {
    name: 'PremiumShowcase',
    props: {
        features: { type: Array, required: true },
        /** Whose name to put the crown on, when we know it. */
        name: { type: String, default: '' },
    },
    created() {
        // Not reactive: nothing renders from them.
        this.observer = null
        this.fallback = null
        this.actEls = null
    },
    data() {
        return {
            // One flag per act rather than one for the whole section, so each
            // demo's animation is gated by its own visibility. Sized off
            // `features` up front — Vue 2 can't detect a plain `arr[i] = x`
            // assignment, so revealing an act later goes through `this.$set`
            // rather than relying on this initial shape to still be reactive
            // by then.
            revealed: this.features.map(() => false),
            board: [
                { name: 'mira', wins: 41 },
                { name: 'you', wins: 38, you: true },
                { name: 'devon', wins: 30 },
                { name: 'sasha', wins: 22 },
            ],
        }
    },
    computed: {
        displayName() { return this.name || 'you' },

        /**
         * Free against premium for one session's play. The premium number is
         * derived rather than typed so it can't drift from the promise the
         * feature copy makes.
         */
        xpBars() {
            return [
                { label: 'Free', value: XP_BASE, width: '46%', premium: false },
                { label: 'Premium', value: XP_BASE * 2, width: '92%', premium: true },
            ]
        },

        /**
         * A 20-node stand-in for the 50-level track. The free row is sparse on
         * purpose — that sparseness *is* the pitch, and it's proportionally
         * honest: 20 of 50 levels pay a free player, against all 50 for a
         * subscriber. Counts and ratio match LEVEL_REWARDS in
         * server/services/rewards.js as of the v3.8.0 track rebuild — recount
         * both if that table's shape changes again.
         */
        trackRows() {
            const nodes = 20
            const free = []
            const premium = []
            for (let i = 0; i < nodes; i++) {
                // 2 filled per 5, matching 20-in-50 (40%).
                free.push(i % 5 < 2)
                premium.push(true)
            }
            return [
                { label: 'Free', nodes: free, count: '20 rewards', premium: false },
                { label: 'Premium', nodes: premium, count: '51 rewards', premium: true },
            ]
        },
    },
    methods: {
        /** Staggered fill, left to right, so the row reads as filling in. */
        nodeDelay(i) {
            return { transitionDelay: `${i * 45}ms` }
        },

        /**
         * Rows start displaced and settle into rank order, which reads as the
         * board sorting itself rather than one block sliding.
         */
        rowShuffle(i) {
            return { transitionDelay: `${i * 90}ms` }
        },

        /**
         * Reveals one act, once, and stops watching it. Once every act has
         * been revealed there's nothing left for the observer to do, so it's
         * torn down rather than left running for the lifetime of the page.
         */
        revealAct(index) {
            if (this.revealed[index]) return
            this.$set(this.revealed, index, true)

            if (this.observer && this.actEls) {
                this.observer.unobserve(this.actEls[index])
            }
            if (this.revealed.every(Boolean)) this.teardown()
        },

        /**
         * Reveals every act at once. For the two paths where staging them
         * individually would serve no one: reduced motion (there's no
         * animation to stage, only a finished state to show) and the
         * document-hidden fallback (by the time that fires, the point was
         * never which act the visitor had scrolled to).
         */
        revealAll() {
            this.revealed = this.features.map(() => true)
            this.teardown()
        },

        teardown() {
            if (this.observer) {
                this.observer.disconnect()
                this.observer = null
            }
            if (this.fallback) {
                clearTimeout(this.fallback)
                this.fallback = null
            }
        },
    },
    mounted() {
        // Reduced motion skips straight to the finished state — no observer,
        // because there's nothing left to wait for.
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (reduced || !window.IntersectionObserver) {
            this.revealAll()
            return
        }

        // ref="acts" set identically on every v-for iteration is Vue's
        // standard pattern for collecting a whole list into one array —
        // $refs.acts is the four act elements, in source order, not one
        // shared node.
        const acts = this.$refs.acts || []
        this.actEls = acts

        // One observer, watching all four acts, each judged on its own
        // visibility rather than all four sharing a single trigger point
        // somewhere in the middle of the section. 0.6 rather than a lower
        // fraction: the act needs to be substantially on screen — "mostly",
        // not "at all" — before it's worth revealing.
        this.observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue
                const index = acts.indexOf(entry.target)
                if (index !== -1) this.revealAct(index)
            }
        }, { threshold: 0.6 })
        acts.forEach(el => this.observer.observe(el))

        // Insurance for the one case an IntersectionObserver is known to
        // never fire at all: a document that's hidden and stays that way — a
        // backgrounded tab, or a headless/preview render. Two of these demos
        // hide their content at opacity 0 until they're live, so an observer
        // that never fires doesn't cost an animation, it costs the
        // leaderboard and half the reward track outright.
        //
        // Scoped to that one case rather than firing unconditionally, which
        // is what this used to do and which was its own bug: most visitors
        // take longer than a few seconds to scroll past the hero, so by the
        // time anyone actually reached the showcase, everything had already
        // revealed off-screen and there was nothing left to watch animate.
        if (document.hidden) {
            this.fallback = setTimeout(this.revealAll, 4000)
        }
    },
    beforeDestroy() {
        this.teardown()
    },
}
</script>

<style scoped lang="scss">
.showcase { overflow: hidden; }

.showcase-act {
    // One box, four rows. A rule between them rather than a gap: separate
    // boxes made four unrelated announcements, where this is one argument
    // made four times.
    + .showcase-act {
        margin-top: 2.5rem;
        padding-top: 2.5rem;
        border-top: 1px solid rgba(127, 127, 127, 0.18);

        @include mobile { margin-top: 2rem; padding-top: 2rem; }
    }

    // Alternating sides, so four rows don't read as a form.
    //
    // No touch override needed: `is-desktop` leaves .columns as a plain block
    // below 1024, so there's no row to reverse. The override this replaces was
    // scoped inside .is-flipped and so applied to every *other* act — acts 2
    // and 4 stacked while 1 and 3 stayed side by side, which is the layout
    // inconsistency this rule was supposed to prevent.
    &.is-flipped .columns {
        flex-direction: row-reverse;
    }
}

.showcase-eyebrow {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-weight: bold;
    opacity: 0.6;
    margin-bottom: 0.5rem;
}

.showcase-title.title {
    margin-bottom: 0.5rem;
    // Nothing wraps from tablet up any more, but a phone can't fit "40 more
    // rewards a season" on one line at this size. When it does wrap, split it
    // evenly rather than dropping a single orphan word. Ignored by browsers
    // that don't support it, which just get the default wrap.
    text-wrap: balance;
}

.showcase-body {
    font-size: 0.95rem;
    opacity: 0.72;
}

// The demos share a recessed panel so they read as a screen rather than more
// page. $background is the page's own grey, which keeps this from becoming a
// third surface colour.
.demo {
    background: $background;
    border-radius: 8px;
    padding: 1.25rem;
}

.demo-caption {
    margin-top: 0.9rem;
    text-align: center;
    opacity: 0.55;
}

// Double XP

.xp-row {
    display: grid;
    grid-template-columns: 5.5rem 1fr 5rem;
    align-items: center;
    gap: 0.75rem;

    + .xp-row { margin-top: 0.9rem; }

    @include mobile {
        grid-template-columns: 1fr;
        gap: 0.25rem;
    }
}

.xp-label {
    font-weight: bold;
    font-size: 0.85rem;
}

.xp-track {
    height: 0.6rem;
    border-radius: 999px;
    background: rgba(127, 127, 127, 0.18);
    overflow: hidden;
}

.xp-fill {
    height: 100%;
    border-radius: 999px;
    background: $grey-light;
    width: 0;
    transition: width 1.1s cubic-bezier(0.22, 1, 0.36, 1);

    &.is-premium {
        background: $gold;
        // The premium bar arrives second, so the difference is something you
        // watch happen rather than something you have to compare.
        transition-delay: 0.35s;
    }
}

.xp-value {
    font-weight: bold;
    font-size: 0.85rem;
    text-align: right;
    opacity: 0.7;

    @include mobile { text-align: left; }
}

// Reward track

.track-row {
    display: grid;
    grid-template-columns: 5.5rem 1fr 5.5rem;
    align-items: center;
    gap: 0.75rem;

    + .track-row { margin-top: 1rem; }

    @include mobile {
        grid-template-columns: 1fr;
        gap: 0.35rem;
    }
}

.track-label {
    font-weight: bold;
    font-size: 0.85rem;
}

.track-nodes {
    display: flex;
    gap: 0.3rem;
    align-items: center;
}

.track-node {
    flex: 1 1 0;
    height: 0.75rem;
    border-radius: 3px;
    background: rgba(127, 127, 127, 0.16);

    &.is-filled {
        // Held at nothing until the section is live, then popped in on the
        // stagger from nodeDelay().
        transform: scaleY(0.2);
        opacity: 0;
        transition: transform 0.35s cubic-bezier(0.22, 1.4, 0.5, 1), opacity 0.35s ease;
        background: $grey;
    }

    &.is-filled.is-premium { background: $gold; }
}

.is-live .track-node.is-filled {
    transform: scaleY(1);
    opacity: 1;
}

.track-count {
    font-weight: bold;
    font-size: 0.85rem;
    text-align: right;
    opacity: 0.7;

    @include mobile { text-align: left; }
}

// Crown

.demo-crown { text-align: center; }

.crown-card {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    background: $white;
    border-radius: 999px;
    padding: 0.5rem 1rem 0.5rem 0.5rem;
}

.crown-avatar {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: $gamebot-gradient;
}

.crown-name {
    font-weight: bold;
}

// The brand's crown, not FontAwesome's. It carries its own gold and its own
// grain, so there's no `color` here to set — sizing is the only job.
.crown-mark {
    display: inline-block;
    width: 1.1rem;
    height: auto;
    vertical-align: baseline;
    // Drops onto the name from just above with a bit of overshoot — a crown
    // is put *on* something, where the old star simply appeared.
    transform: translateY(-0.6rem) scale(0.4);
    opacity: 0;
    transition: transform 0.5s cubic-bezier(0.22, 1.6, 0.4, 1), opacity 0.3s ease;
    transition-delay: 0.25s;
}

.is-live .crown-mark {
    transform: none;
    opacity: 1;
}

// Leaderboard

.board {
    list-style: none;
    margin: 0;
}

.board-row {
    display: grid;
    grid-template-columns: 1.5rem 1fr auto;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    background: $white;
    font-size: 0.9rem;

    // Off their marks until live, then they settle into rank order.
    opacity: 0;
    transform: translateY(0.75rem);
    transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease;

    + .board-row { margin-top: 0.4rem; }

    &.is-you {
        outline: 2px solid $gold;
        font-weight: bold;
    }
}

.is-live .board-row {
    opacity: 1;
    transform: none;
}

.board-rank {
    font-weight: bold;
    opacity: 0.45;
    text-align: center;
}

.board-wins {
    font-weight: bold;
    opacity: 0.7;
}

// Reduced motion: revealAll() applies `is-live` to every act on mount, so
// all that's left is to stop anything easing into place.
@media (prefers-reduced-motion: reduce) {
    .xp-fill,
    .track-node.is-filled,
    .crown-mark,
    .board-row {
        transition: none !important;
        transition-delay: 0s !important;
    }
}
</style>
